// The dashboard chat's own runs, read back from the org's trace store.
//
// Every LLM request the chat makes is captured as an AiPrompt event whose
// body is the provider request: the system prompt, and the messages of the
// conversation so far. The last request of a turn therefore carries the whole
// run: the question, every tool call with its input, every tool result. This
// module turns such a body back into what the recipe distiller expects (the
// trace, the rows per query, the graph with its claims, the question and the
// cost), and reads the recipes a run saved through `save_recipe` straight out
// of the transcript, without re-distilling anything.
//
// It knows the provider (Anthropic) message shape the proxy stores, not the
// agent's own; the agent's shapes are built by graphRecipes.saveRecipeMessages.
import { createGraph } from "./investigationGraph.js";
import { feedGraph, rowsFromContent } from "./investigationGraphRun.js";
import { traceEntry } from "./graphRecipes.js";

export const RUN_MARKER = "investigation_graph";
const READER_CLAIM = /^\s*-?\s*(c\d+)\s+(attribution|activity|presence|finding)\s+(supported|insufficient)(\s+\[context\])?:\s*(.*)$/;

export function textOf(content) {
    if (typeof content === "string") return content;
    if (!Array.isArray(content)) return "";
    return content.filter((b) => b && b.type === "text" && typeof b.text === "string").map((b) => b.text).join("\n");
}

function isToolResultTurn(message) {
    return message?.role === "user" && Array.isArray(message.content) && message.content.some((b) => b?.type === "tool_result");
}

// The turn a body belongs to: the last user message that is text, not tool
// results, and everything after it.
export function turnStart(messages) {
    for (let i = messages.length - 1; i >= 0; i--) {
        const m = messages[i];
        if (m?.role === "user" && !isToolResultTurn(m) && textOf(m.content).trim()) return i;
    }
    return 0;
}

// Rows from an analytics result's text. The result is JSON with columns and
// rows; anything wrapped around it is skipped.
export function rowsFromResultText(text) {
    const tryParse = (t) => { try { return rowsFromContent({ content: [{ type: "text", text: t }] }); } catch { return null; } };
    const direct = tryParse(text);
    if (direct && direct.rows.length) return direct;
    const i = text.indexOf("{"), j = text.lastIndexOf("}");
    const inner = i >= 0 && j > i ? tryParse(text.slice(i, j + 1)) : null;
    return inner || direct || { rows: [], total: 0 };
}

// A run as the distiller sees it, from one provider request body (an object
// or its JSON text).
export function reconstructRun(body) {
    const req = typeof body === "string" ? JSON.parse(body) : body;
    const messages = Array.isArray(req?.messages) ? req.messages : [];
    const start = turnStart(messages);
    const question = textOf(messages[start]?.content).trim();
    const results = new Map();
    for (const m of messages.slice(start)) {
        if (!isToolResultTurn(m)) continue;
        for (const b of m.content) if (b?.type === "tool_result") results.set(b.tool_use_id, { text: textOf(b.content), isError: Boolean(b.is_error) });
    }
    const graph = createGraph();
    const rowsById = new Map();
    const trace = [];
    const saves = [];
    let calls = 0;
    let recipeCalls = 0;
    for (const m of messages.slice(start)) {
        if (m?.role !== "assistant" || !Array.isArray(m.content)) continue;
        for (const b of m.content) {
            if (b?.type !== "tool_use") continue;
            calls++;
            const res = results.get(b.id) || { text: "", isError: true };
            const ok = !res.isError && !/^error\b/i.test(res.text.trim());
            if (b.name === "run_recipe") recipeCalls++;
            trace.push(traceEntry(b.name, b.id, b.input || {}, ok));
            if ((b.name === "analytics_query" || b.name === "pivot") && ok) {
                const { rows } = rowsFromResultText(res.text);
                if (rows.length && b.input?.spec) { try { feedGraph(graph, { toolCallId: b.id, spec: b.input.spec, objects: rows }); } catch { /* a spec the graph cannot read still counts as a call */ } }
                rowsById.set(b.id, rows);
            } else if ((b.name === "expand" || b.name === "expand_all") && ok) {
                const anchor = b.input?.anchor || null;
                for (const line of res.text.split("\n")) {
                    const mm = READER_CLAIM.exec(line);
                    if (mm) graph.claims.push({ id: `${b.id}:${mm[1]}`, kind: mm[2], status: mm[3], bearing: mm[4] ? "context" : "answers", statement: mm[5], evidence: anchor ? [anchor] : [], source: { toolCallId: b.id, leaf: true } });
                }
            } else if (b.name === "claim" && ok) {
                let status = /insufficient/i.test(res.text) ? "insufficient" : "supported";
                try { const parsed = JSON.parse(res.text); if (parsed?.status) status = parsed.status; } catch { /* the text form is enough */ }
                graph.claims.push({ id: `${b.id}:c`, kind: b.input?.kind || "finding", status, bearing: "answers", statement: b.input?.statement || "", evidence: Array.isArray(b.input?.evidence) ? b.input.evidence : [], source: { toolCallId: b.id } });
            } else if (b.name === "save_recipe" && ok) {
                const saved = savedRecipeFromResult(res.text, b.input);
                if (saved) saves.push(saved);
            }
        }
    }
    return { question, trace, graph, rowsById, getRows: (id) => rowsById.get(id) || null, calls, recipeCalls, saves, messages: messages.length };
}

// A recipe the run saved: the result of a save_recipe call carries it whole.
export function savedRecipeFromResult(text, input = null) {
    try {
        const parsed = JSON.parse(text);
        const recipe = parsed?.saved;
        if (recipe && recipe.id && Array.isArray(recipe.steps)) return { recipe, stored: parsed.stored || null, outcome: parsed.outcome || input?.outcome || null, notes: Array.isArray(parsed.notes) ? parsed.notes : [] };
    } catch { /* not a save the harness wrote */ }
    return null;
}

// The recipes saved anywhere in a body's messages, whether or not the run in
// question is the last turn.
export function savesFromBody(body) {
    const req = typeof body === "string" ? JSON.parse(body) : body;
    const messages = Array.isArray(req?.messages) ? req.messages : [];
    const results = new Map();
    for (const m of messages) if (isToolResultTurn(m)) for (const b of m.content) if (b?.type === "tool_result") results.set(b.tool_use_id, textOf(b.content));
    const out = [];
    for (const m of messages) {
        if (m?.role !== "assistant" || !Array.isArray(m.content)) continue;
        for (const b of m.content) {
            if (b?.type !== "tool_use" || b.name !== "save_recipe") continue;
            const saved = savedRecipeFromResult(results.get(b.id) || "", b.input);
            if (saved) out.push(saved);
        }
    }
    return out;
}

// The SQL that finds the last request of each recent run: requests of one
// run share their first two messages (the question and the model's first
// reply), and the one with the most messages carries the whole turn.
export function runsQuery({ days = 2, limit = 40, marker = RUN_MARKER } = {}) {
    return `WITH JSONExtractString(toString(event_data), 'api_request') AS body
SELECT endpoint_id, cityHash64(substring(JSONExtractRaw(body, 'messages', 1), 1, 400), substring(JSONExtractRaw(body, 'messages', 2), 1, 400)) AS run_key, argMax(event_id, JSONLength(body, 'messages')) AS event_id, max(JSONLength(body, 'messages')) AS n_messages, max(event_timestamp) AS at
FROM events.events WHERE event_type = 'AiPrompt' AND event_timestamp > now() - INTERVAL ${Number(days) || 2} DAY AND positionCaseInsensitive(toString(event_data), '${marker}') > 0 AND JSONLength(body, 'messages') >= 3
GROUP BY endpoint_id, run_key ORDER BY at DESC LIMIT ${Number(limit) || 40}`;
}

export function bodiesQuery({ eventIds, days = 2 }) {
    // Only ids shaped like the event ids they are; anything else is dropped rather than quoted.
    const ids = eventIds.filter((id) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(id))).map((id) => `'${String(id).toLowerCase()}'`).join(",");
    return `SELECT event_id, event_timestamp, endpoint_id, JSONExtractString(toString(event_data), 'api_request') AS body FROM events.events WHERE event_type = 'AiPrompt' AND event_timestamp > now() - INTERVAL ${Number(days) || 2} DAY AND event_id IN (${ids})`;
}
